import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { getNewsletter, setNewsletter, PreferenciasDaNewsletter } from '../services/api';
import { useTravarScroll, useFocoNoModal } from '../utils/travarScroll';
import { useFechavelComVoltar } from '../contexts/VoltarDoCelular';
import './NewsletterModal.css';

/**
 * A newsletter diária: ligar, desligar e escolher os temas das notícias.
 *
 * Abre só quando ela pede — pelo menu do avatar ou pelo "Trocar temas" do
 * próprio email (`?abrir=newsletter`). Nunca sozinha ([[sem-modais-em-fila]]).
 *
 * A promessa da tela é a do email: a análise do dinheiro dela em linguagem
 * simples, o que dá para melhorar com o que o app já tem, e as notícias dos
 * temas que ela marcar. Dias úteis, de manhã.
 */
interface Props { onClose: () => void; userId?: string }

/*
 * As preferências, guardadas por conta (22/10). Antes o modal buscava a cada
 * abertura e mostrava "Carregando…" para uma lista que quase nunca muda. Agora
 * abre com o que já veio e confere em silêncio; o menu do avatar já busca
 * antes, então nem a primeira abertura espera.
 */
const guardadas = new Map<string, PreferenciasDaNewsletter>();
const buscando = new Map<string, Promise<PreferenciasDaNewsletter>>();

export const buscarNewsletter = (userId = ''): Promise<PreferenciasDaNewsletter> => {
  const emCurso = buscando.get(userId);
  if (emCurso) return emCurso;
  const pedido = getNewsletter()
    .then(p => { guardadas.set(userId, p); return p; })
    .finally(() => buscando.delete(userId));
  buscando.set(userId, pedido);
  return pedido;
};

/*
 * Como ela não cai em Promoções nem no spam. O mesmo passo a passo que as três
 * primeiras newsletters trazem no topo (`backend/.../newsletter/montar.ts`,
 * `PASSOS_DA_CAIXA`); os pacotes não compartilham módulo.
 */
const PASSOS_DA_CAIXA = [
  'Adicione noreply@fintracking.com.br aos seus contatos.',
  'No Gmail, se ela cair em Promoções: arraste o email para a aba Principal e toque em "Sim". No celular: menu ⋮ → Mover para → Principal.',
  'Se cair no spam: abra e toque em "Não é spam".',
];

const NewsletterModal: React.FC<Props> = ({ onClose, userId = '' }) => {
  const jaVeio = guardadas.get(userId) ?? null;
  const [prefs, setPrefs] = useState<PreferenciasDaNewsletter | null>(jaVeio);
  const [ativa, setAtiva] = useState(jaVeio?.ativa ?? false);
  const [temas, setTemas] = useState<string[]>(jaVeio?.temas ?? []);
  const mexeu = useRef(false);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [salvo, setSalvo] = useState(false);
  const caixa = useRef<HTMLDivElement>(null);

  useTravarScroll();
  useFocoNoModal(caixa);
  useFechavelComVoltar(true, onClose);

  useEffect(() => {
    buscarNewsletter(userId)
      .then(p => {
        setPrefs(p);
        // A conferência não desfaz o que ela já começou a marcar.
        if (!mexeu.current) { setAtiva(p.ativa); setTemas(p.temas); }
      })
      .catch(() => { if (!guardadas.has(userId)) setErro('Não consegui carregar suas preferências agora.'); });
  }, [userId]);

  useEffect(() => {
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [onClose]);

  const alternar = (id: string) => {
    mexeu.current = true;
    setSalvo(false);
    setTemas(t => (t.includes(id) ? t.filter(x => x !== id) : [...t, id]));
  };

  const salvar = async () => {
    setSalvando(true);
    setErro(null);
    try {
      await setNewsletter(ativa, temas);
      if (prefs) guardadas.set(userId, { ...prefs, ativa, temas });
      setSalvo(true);
    } catch {
      setErro('Não consegui salvar. Tente de novo.');
    } finally {
      setSalvando(false);
    }
  };

  const corpo = (
    <div className="nl-overlay" onClick={onClose} ref={caixa}>
      <div className="nl-modal" onClick={e => e.stopPropagation()}
           role="dialog" aria-modal="true" aria-labelledby="nl-titulo">
        <div className="nl-banda">
          <h2 id="nl-titulo">Newsletter diária</h2>
          <button className="nl-fechar" onClick={onClose} aria-label="Fechar">✕</button>
        </div>

        <div className="nl-corpo">
          <p className="nl-texto">
            Todo dia útil de manhã, no seu email: o que os seus números estão dizendo,
            o que dá para melhorar, o que vence no dia e as notícias dos temas que você escolher.
          </p>

          {!prefs && !erro && <p className="nl-nota">Carregando…</p>}

          {prefs && !prefs.disponivel && (
            <p className="nl-nota">A newsletter ainda está sendo preparada. Volte em breve.</p>
          )}

          {prefs?.disponivel && (
            <>
              <label className="nl-interruptor">
                <input type="checkbox" checked={ativa}
                       onChange={e => { mexeu.current = true; setAtiva(e.target.checked); setSalvo(false); }} />
                <span>Quero receber a newsletter</span>
              </label>

              <fieldset className="nl-temas" disabled={!ativa}>
                <legend>Notícias sobre</legend>
                {prefs.opcoes.map(o => (
                  <label key={o.id} className={`nl-tema${temas.includes(o.id) ? ' nl-tema-on' : ''}`}>
                    <input type="checkbox" checked={temas.includes(o.id)} onChange={() => alternar(o.id)} />
                    {o.nome}
                  </label>
                ))}
                <p className="nl-nota">
                  {temas.length === 0
                    ? 'Sem tema marcado, a newsletter vem só com as suas finanças.'
                    : temas.length > 8
                      ? 'Cada notícia vem com um resumo curto e o link para ler a matéria completa. Com mais de 8 temas, eles se revezam ao longo da semana.'
                      : 'Cada notícia vem com um resumo curto e o link para ler a matéria completa.'}
                </p>
              </fieldset>

              <button className="nl-btn" onClick={salvar} disabled={salvando} aria-busy={salvando}>
                Salvar
              </button>
              {salvo && (
                <p className="nl-ok" role="status">
                  {ativa ? 'Pronto. A próxima chega no próximo dia útil, de manhã.' : 'Pronto. Você não recebe mais a newsletter.'}
                </p>
              )}

              {/* Aberto logo depois de ligar; fechado, e à mão, para quem já recebe. */}
              {(ativa || prefs.ativa) && (
                <details className="nl-caixa" open={salvo && ativa}>
                  <summary>Para chegar na Caixa Principal</summary>
                  <ol>
                    {PASSOS_DA_CAIXA.map(p => <li key={p}>{p}</li>)}
                  </ol>
                </details>
              )}
            </>
          )}

          {erro && <p className="nl-erro" role="alert">{erro}</p>}
        </div>
      </div>
    </div>
  );

  return createPortal(corpo, document.body);
};

export default NewsletterModal;
